import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Group, Text } from '@repletoryreach/sdk-ui';
import { useT } from '../../i18n/I18nProvider';
import { GuideText } from './GuideText';
import type { Guide, GuideLink, TourStep } from './types';

const CARD_W = 380;
const GAP = 14;
const EDGE = 16;

type Box = { top: number; left: number; width: number; height: number };

/**
 * Waits for a marked element on the page, for pages that load their data first. `names` may list several,
 * comma-separated, best first: a button only an admin sees, then the list it adds to.
 */
function findTarget(names: string, timeoutMs: number): Promise<HTMLElement | null> {
  return new Promise((resolve) => {
    const start = Date.now();
    const look = () => {
      for (const name of names.split(',')) {
        const el = document.querySelector<HTMLElement>(`[data-guide="${name.trim()}"]`);
        if (el && el.getClientRects().length > 0) return resolve(el);
      }
      if (Date.now() - start > timeoutMs) return resolve(null);
      window.setTimeout(look, 120);
    };
    look();
  });
}

/** Whether the page is the step's: the same path, and every query value the step names (a settings tab). */
function onRoute(route: string, pathname: string, search: string): boolean {
  const [path, query] = route.split('?');
  if (path !== pathname) return false;
  if (!query) return true;
  const here = new URLSearchParams(search);
  return [...new URLSearchParams(query)].every(([k, v]) => here.get(k) === v);
}

/** Where the card goes: under the element, else above it, else in the middle; always inside the window. */
function place(box: Box | null, cardH: number): { top: number; left: number; centred: boolean } {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const w = Math.min(CARD_W, vw - EDGE * 2);
  if (!box || vw < 640) return { top: Math.max(EDGE, (vh - cardH) / 2), left: (vw - w) / 2, centred: true };
  const left = Math.min(Math.max(EDGE, box.left + box.width / 2 - w / 2), vw - w - EDGE);
  if (box.top + box.height + GAP + cardH <= vh - EDGE) return { top: box.top + box.height + GAP, left, centred: false };
  if (box.top - GAP - cardH >= EDGE) return { top: box.top - GAP - cardH, left, centred: false };
  // A tall part (a list, a board): beside it when there is room, so the part itself stays usable.
  const top = Math.min(Math.max(EDGE, box.top), vh - cardH - EDGE);
  if (box.left + box.width + GAP + w <= vw - EDGE) return { top, left: box.left + box.width + GAP, centred: false };
  if (box.left - GAP - w >= EDGE) return { top, left: box.left - GAP - w, centred: false };
  // No room anywhere: the bottom right corner, over as little of the part's top as possible.
  return { top: vh - cardH - EDGE, left: vw - w - EDGE, centred: false };
}

/**
 * The tour: a dimmed page with the step's element lit up, and a card beside it. Steps can be on other pages; the
 * tour opens each page in turn. Escape or Skip ends it; the arrow keys move between steps.
 */
export function GuideTour({ guide, steps, onClose, onOpenGuide, onStartTour, onStep }: {
  guide: Guide; steps: TourStep[]; onClose: () => void; onOpenGuide: () => void; onStartTour: (tourId: string) => void;
  /** Told each step as it is shown. */
  onStep?: (step: TourStep) => void;
}) {
  const t = useT();
  const nav = useNavigate();
  const { pathname, search } = useLocation();
  const [index, setIndex] = useState(0);
  const [box, setBox] = useState<Box | null>(null);
  const [ready, setReady] = useState(false);
  const [cardH, setCardH] = useState(240);
  const cardRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const targetRef = useRef<HTMLElement | null>(null);
  const movedFor = useRef(-1);
  const step = steps[index];
  const last = index === steps.length - 1;
  const reportStep = useRef(onStep);
  reportStep.current = onStep;
  useEffect(() => { if (step) reportStep.current?.(step); }, [step]);

  const measure = useCallback(() => {
    const el = targetRef.current;
    if (!el || !el.isConnected) { setBox(null); return; }
    const r = el.getBoundingClientRect();
    const next = { top: r.top - 6, left: r.left - 6, width: r.width + 12, height: r.height + 12 };
    setBox((prev) => (prev && Math.abs(prev.top - next.top) < 0.5 && Math.abs(prev.left - next.left) < 0.5
      && Math.abs(prev.width - next.width) < 0.5 && Math.abs(prev.height - next.height) < 0.5 ? prev : next));
  }, []);

  // Open the step's page, then wait for its element.
  useEffect(() => {
    let cancelled = false;
    setReady(false);
    targetRef.current = null;
    if (step.route) {
      // Each step opens its page once, or finds the person already there. When they then go on from it themselves
      // (they opened the ticket the step pointed at), the tour goes with them rather than pulling them back.
      if (onRoute(step.route, pathname, search)) movedFor.current = index;
      else if (movedFor.current !== index) { movedFor.current = index; nav(step.route); return; }
    }
    (async () => {
      let el = step.target ? await findTarget(step.target, step.open ? 300 : 2500) : null;
      // The step explains something that is not open yet (a form): open it, then look again.
      if (!el && step.open && !cancelled) {
        const opener = await findTarget(step.open, 2500);
        if (opener && !cancelled) { opener.click(); el = step.target ? await findTarget(step.target, 2500) : null; }
      }
      if (cancelled) return;
      targetRef.current = el;
      if (el) {
        el.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'auto' });
        await new Promise((r) => window.setTimeout(r, 60));
      }
      if (cancelled) return;
      measure();
      setReady(true);
    })();
    return () => { cancelled = true; };
  }, [step, index, pathname, search, nav, measure]);

  // Follow the element every frame: pages scroll, cards animate in and data arrives after the step has opened.
  useEffect(() => {
    if (!ready) return;
    let frame = 0;
    const tick = () => { measure(); frame = window.requestAnimationFrame(tick); };
    frame = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(frame);
  }, [ready, measure]);

  useLayoutEffect(() => {
    if (cardRef.current) setCardH(cardRef.current.offsetHeight);
  }, [index, ready]);

  useEffect(() => { if (ready) headingRef.current?.focus(); }, [ready, index]);

  const next = useCallback(() => (last ? onClose() : setIndex((i) => i + 1)), [last, onClose]);
  const back = useCallback(() => setIndex((i) => Math.max(0, i - 1)), []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Escape ends the tour only: a form the tour opened stays open, so it is caught before the form hears it.
      if (e.key === 'Escape') { e.preventDefault(); e.stopImmediatePropagation(); onClose(); return; }
      // The arrows belong to whatever the person is typing in.
      const el = e.target as HTMLElement | null;
      if (el && (el.isContentEditable || ['INPUT', 'SELECT', 'TEXTAREA'].includes(el.tagName))) return;
      if (e.key === 'ArrowRight') next();
      else if (e.key === 'ArrowLeft') back();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [next, back, onClose]);

  const pos = place(box, cardH);
  const follow = (l: GuideLink) => {
    onClose();
    if (l.tour) onStartTour(l.tour); else if (l.to) nav(l.to);
  };

  return createPortal(
    <div className="rr-tour" data-testid="guide-tour">
      {/* Clicks on the page are held while the tour is open, so a stray click does not change anything; the lit
          element itself stays usable, so a form can be filled in as the tour explains it. */}
      {box && ready ? (
        <>
          <div className="rr-tour__blocker" aria-hidden style={{ top: 0, left: 0, right: 0, height: Math.max(0, box.top) }} />
          <div className="rr-tour__blocker" aria-hidden style={{ top: box.top + box.height, left: 0, right: 0, bottom: 0 }} />
          <div className="rr-tour__blocker" aria-hidden style={{ top: box.top, left: 0, width: Math.max(0, box.left), height: box.height }} />
          <div className="rr-tour__blocker" aria-hidden style={{ top: box.top, left: box.left + box.width, right: 0, height: box.height }} />
        </>
      ) : <div className="rr-tour__blocker rr-tour__blocker--all" aria-hidden />}
      {box && ready
        ? <div className="rr-tour__spot" aria-hidden style={{ top: box.top, left: box.left, width: box.width, height: box.height }} />
        : <div className="rr-tour__dim" aria-hidden />}
      <div
        ref={cardRef}
        className={`rr-tour__card${pos.centred ? ' rr-tour__card--centred' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="rr-tour-title"
        style={{ top: pos.top, left: pos.left, width: Math.min(CARD_W, window.innerWidth - EDGE * 2), visibility: ready ? 'visible' : 'hidden' }}
      >
        <Text role="caption" c="var(--rr-color-text-muted)">
          {t('guide.tour.stepOf', { n: index + 1, total: steps.length })}
        </Text>
        <h2 id="rr-tour-title" ref={headingRef} tabIndex={-1} className="rr-tour__title">{t(step.titleKey)}</h2>
        <div className="rr-tour__body"><GuideText text={t(step.bodyKey)} /></div>
        {step.links && step.links.length > 0 && (
          <div className="rr-tour__links">
            {step.links.map((l) => (
              <button key={(l.to ?? l.tour ?? '') + l.labelKey} type="button" className="rr-guide__link" onClick={() => follow(l)}>{t(l.labelKey)}</button>
            ))}
          </div>
        )}
        <div className="rr-tour__progress" aria-hidden>
          {steps.map((_, i) => <span key={i} className={i === index ? 'is-on' : i < index ? 'is-done' : ''} />)}
        </div>
        <Group justify="space-between" gap="xs" wrap="nowrap">
          <Button size="xs" variant="subtle" onClick={onClose}>{last ? t('guide.tour.close') : t('guide.tour.skip')}</Button>
          <Group gap="xs" wrap="nowrap">
            {last && <Button size="xs" variant="default" onClick={() => { onClose(); onOpenGuide(); }}>{t('guide.tour.readGuide')}</Button>}
            {index > 0 && <Button size="xs" variant="default" onClick={back}>{t('guide.tour.back')}</Button>}
            {!last && <Button size="xs" onClick={next} data-testid="guide-next">{t('guide.tour.next')}</Button>}
          </Group>
        </Group>
      </div>
    </div>,
    document.body
  );
}
